/**
 * gengql documents for the P1 engagement surfaces (ENG-5448 / FE2a).
 *
 * §6.3 merge gate: no take-rate field is ever *declared* here. The account take
 * rates on `EngagementBillingSettings` are staff-gated server-side and resolve
 * null for a customer-side viewer, but the invariant is that the client never
 * asks — a selection set is a contract, and a customer surface asking for take
 * rates is the leak, not the null it happens to get back.
 *
 * `expertOfferContext` is the one bracket-shaped field here, and it is
 * deliberate (ENG-5604 G7). It carries hour thresholds only — no money, no take
 * rate — and the api gates it to the expert, resolving null for everyone else.
 * It exists so the §9.11 rate nudge can trigger from expert-visible data instead
 * of reaching through `primaryProject` into the customer's billing account,
 * which is both denied to the expert and the wrong side of the wall. Per
 * §6.7/§6.3 the rendered nudge copy never mentions the thresholds themselves.
 *
 * Money fields are viewer-segregated by the resolvers: `expertRate` /
 * `expertEarnings` are null for customer-side viewers and `customerRateLoaded`
 * / `nteTotal` are null for the expert. A null here is a view decision, never
 * an error — see `viewModel.ts` and plan §5.
 */
import { gengql } from '@/__generated__';

export const ENGAGEMENT_HUB = gengql(/* GraphQL */ `
  query engagementHub($id: ID!) {
    engagement(id: $id) {
      id
      status
      viewerIsOfStaff
      awaiting
      creationPath
      requiresGovernance
      governanceRoleConflated
      fundingMethod
      nteHoursTotal
      nteHoursConsumed
      hoursPending
      nteConsumedPercent
      hasAcceptedAmendments
      hasBillingActivity
      viewerCanClose
      expertId
      expertIdentity {
        userId
        name
        pictureUrl
      }
      customerIdentities {
        userId
        name
        pictureUrl
      }
      customerOrganizationName
      createdByUserId
      primaryProjectId
      primaryProjectName
      expertOfferMadeAt
      customerConfirmedAt
      governanceApprovedAt
      closingInitiatedAt
      cancellationWindowEndsAt
      closedAt
      closureReason
      createdAt
      updatedAt
      requestScopeDescription
      offerScopeDescription
      startDate
      endDate
      billingCadence
      pacingType
      pacingValue
      expertDeclineNote
      governanceFeedback {
        kind
        note
        decidedAt
        proposedNteTotal {
          cents
          currency
        }
      }
      expertRate {
        cents
        currency
      }
      expertEarnings {
        cents
        currency
      }
      customerRateLoaded {
        cents
        currency
      }
      nteTotal {
        cents
        currency
      }
      rateNudgeDismissed
      expertOfferContext {
        engagementMinHours
        reducedThresholdHours
        longTermThresholdHours
      }
      participants {
        userId
        role
      }
      messageChannels {
        id
        channelType
        lastMessageAt
        members {
          userId
          joinedAt
          identity {
            userId
            name
            pictureUrl
          }
        }
      }
      primaryProject {
        id
        name
        html_url
        tracking_code
        members {
          id
          role
          state
          user {
            id
            name
            first_name
            last_name
            picture_url
          }
        }
        group {
          id
          name
          billing_account {
            id
            isSMBAccount
            type
            engagementBillingSettings {
              billingAccountId
              engagementMinHours
              engagementGovernanceThresholdCredits
              engagementGovernanceThresholdUsd {
                cents
                currency
              }
              governancePolicyType
              overThresholdFundingMethod
              invoiceTermsDays
              hoursAutoApproveDays
              nteAlertPct
              mediationDays
              disputeArbitrationDays
              reminderDays
              autoChargeEnabled
              defaultBillingEmail
            }
          }
        }
      }
      activity(first: 20) {
        edges {
          cursor
          node {
            id
            eventType
            actorUserId
            fromState
            toState
            reason
            createdAt
          }
        }
        pageInfo {
          hasNextPage
        }
      }
      hoursSubmissions {
        id
        engagementId
        expertId
        status
        hoursSubmitted
        hoursConfirmed
        workDescription
        periodStart
        periodEnd
        submittedAt
        confirmedAt
        confirmedByUserId
        confirmNote
        disputeNote
        disputedByUserId
        resolution
        resolvedAt
        resolvedByUserId
        approvedAutomatically
        paidAt
      }
      amendments {
        id
        engagementId
        status
        proposedByUserId
        changeSet
        priorVersionSnapshot
        createdAt
        resolvedAt
        supersedesAmendmentId
        newExpertRate {
          cents
          currency
        }
        priorExpertRate {
          cents
          currency
        }
        newCustomerRateLoaded {
          cents
          currency
        }
        priorCustomerRateLoaded {
          cents
          currency
        }
      }
    }
  }
`);

/**
 * The governance approver's queue — one request for the whole list.
 *
 * Viewer-scoped server-side: the approver identity comes from the session, so
 * the scope *is* the authorization and there is no id to pass. Rows arrive
 * batch-hydrated, which is what keeps `nteTotal` non-null without an
 * `engagement(id)` per row.
 *
 * The selection set is deliberately narrow, and that is load-bearing rather
 * than tidy. `myGovernanceQueue` hydrates `participants`,
 * `governanceRoleConflated`, `primaryProject`, the viewer's money and — since
 * ENG-5612 B2 — `nteHoursConsumed` / `hoursPending` / `nteConsumedPercent` in
 * batch. `hoursSubmissions` is the one metric still **not** batched: its own
 * documentation is explicit that it costs one query per row if selected
 * (`schema.graphql`'s `myGovernanceQueue` docstring; api's
 * `engagementHoursSubmissionsField`). A queue row needs none of it.
 * **Selecting `hoursSubmissions` here turns the queue into an N+1 silently.**
 *
 * The row-identity fields (ENG-5753) are free, and each for its own reason.
 * `expertIdentity` and `createdByIdentity` are batch-stamped by
 * `stampPartyIdentities` on this very resolver, in the one profile lookup for
 * the whole page; the two scope descriptions and `customerConfirmedAt` are
 * columns on the row already in hand; and `primaryProject.group` resolves
 * through the group DataLoader, so the account name is deduped across the page
 * rather than fetched per row. `primaryProject.members` is the one that is NOT
 * — it has no loader, so **do not select it here**: `createdByIdentity` is what
 * names the creator (ENG-5750).
 */
export const MY_GOVERNANCE_QUEUE = gengql(/* GraphQL */ `
  query myGovernanceQueue {
    myGovernanceQueue {
      id
      nteHoursTotal
      nteTotal {
        cents
        currency
      }
      primaryProjectName
      primaryProject {
        id
        group {
          id
          name
        }
      }
      expertIdentity {
        userId
        name
        pictureUrl
      }
      createdByIdentity {
        userId
        name
        pictureUrl
      }
      requestScopeDescription
      offerScopeDescription
      customerConfirmedAt
    }
  }
`);

/**
 * The Approvals nav gate (ENG-5654).
 *
 * Deliberately NOT derived from MY_GOVERNANCE_QUEUE above: that returns
 * hydrated engagements and is unbounded, so using it to decide whether to draw
 * a nav link would cost a full queue read on every page load. This is one
 * boolean the api answers with an EXISTS.
 *
 * Eligibility, not queue contents — an approver who has cleared their queue is
 * still true, so the nav item does not vanish when they catch up.
 */
export const CAN_APPROVE_GOVERNANCE = gengql(/* GraphQL */ `
  query canApproveGovernance {
    canApproveGovernance
  }
`);

export const ENGAGEMENT_MESSAGES = gengql(/* GraphQL */ `
  query engagementMessages(
    $engagementId: String!
    $channelType: EngagementChannelType!
    $cursor: String
  ) {
    engagementMessages(engagementId: $engagementId, channelType: $channelType, cursor: $cursor) {
      hasMore
      nextCursor
      messages {
        messageId
        senderId
        text
        hasAttachment
        attachment {
          filename
          sizeBytes
        }
        sentAt
      }
    }
  }
`);

/**
 * Presigned and short-lived — fetched on click, never eagerly. The label
 * (filename, size) rides `EngagementMessage.attachment` instead, so the thread
 * can name the file without spending one of these on it (ENG-5733).
 */
export const ENGAGEMENT_ATTACHMENT_URL = gengql(/* GraphQL */ `
  query engagementAttachmentUrl(
    $engagementId: String!
    $channelType: EngagementChannelType!
    $messageId: String!
  ) {
    engagementAttachmentUrl(
      engagementId: $engagementId
      channelType: $channelType
      messageId: $messageId
    )
  }
`);

/**
 * The **viewer's own** payout account. The resolver currently ignores the `id`
 * argument and resolves the session user either way, but we still pass the
 * viewer's own id so the call stays correct if it ever starts honouring it —
 * an expert must never be able to read another expert's payout state.
 */
export const ENGAGEMENT_PAYOUT_ACCOUNT = gengql(/* GraphQL */ `
  query engagementPayoutAccount($userId: String!) {
    payoutAccountByUserID(id: $userId) {
      id
      user_id
      payouts_enabled
    }
  }
`);

export const ENGAGEMENT_PAYOUT_ONBOARDING_URL = gengql(/* GraphQL */ `
  mutation engagementPayoutOnboardingURL($userId: String!) {
    createPayoutAccountURL(user_id: $userId, type: account_onboarding)
  }
`);

/** Rate prefill — the same profile field `alsoUpdateProfileRate` writes back to. */
export const ENGAGEMENT_EXPERT_PROFILE_RATE = gengql(/* GraphQL */ `
  query engagementExpertProfileRate($profileId: String!) {
    profile(id: $profileId) {
      id
      bill_rate
    }
  }
`);

/**
 * Hire-drawer context. `engagementMinHours` is the only account setting the
 * creation form validates against — the take rates a price preview needs are
 * staff-gated and deliberately never requested here; `ENGAGEMENT_PRICING_PREVIEW`
 * gets those numbers from the server instead.
 */
export const ENGAGEMENT_CREATION_CONTEXT = gengql(/* GraphQL */ `
  query engagementCreationContext($projectId: String!) {
    project(id: $projectId) {
      id
      name
      # ENG-5727: the api's verdict on whether this viewer may open an
      # engagement on the project they just picked. The consultation entry point
      # reads it from its own page query and explains before the drawer opens;
      # on the profile path this is the first moment a project exists to ask
      # about, so without it the refusal only arrives on submit.
      viewer_engagement_create_denial
      group {
        id
        name
        billing_account {
          id
          isSMBAccount
          engagementBillingSettings {
            billingAccountId
            engagementMinHours
          }
        }
      }
    }
  }
`);

/**
 * The one server answer the hire drawer still needs before submit (ENG-5604 G2,
 * narrowed by ENG-5731).
 *
 * §6.3 in its sharpest form. A price preview needs the expert's underlying rate
 * and the account take rates to compute, and a customer-side viewer must never
 * see either — so the computation happens on the server and
 * `EngagementPricingPreview` carries **only** customer-facing numbers. There is
 * no expert-side field to select here, and none may be added: this is the exact
 * surface where a client-side approximation would have broken the invariant the
 * whole feature is built around.
 *
 * **`requiresGovernance` is now the whole selection set.** ENG-5731 stopped the
 * drawer showing any money before the expert has made an offer: the drawer
 * prices off the expert's *profile* rate, typically their higher consultation
 * rate, and a loaded figure shown pre-offer can lose a deal we are actively
 * steering toward a lower one. The type still answers `nteTotal`,
 * `customerRateLoaded` and `creditsDisplay`, and answered `bands` until api#1861
 * removed it under this same ticket; this document deliberately asks for none of
 * them, so the browser never receives a rate or a total that no longer has
 * anywhere to be displayed.
 *
 * A selection set is a contract — the reason this is a comment and not a
 * deletion is that re-adding a field here is exactly how the figures would come
 * back. `pricing-preview.test.tsx` pins the absence of each one.
 */
export const ENGAGEMENT_PRICING_PREVIEW = gengql(/* GraphQL */ `
  query engagementPricingPreview($input: EngagementPricingPreviewInput!) {
    engagementPricingPreview(input: $input) {
      requiresGovernance
    }
  }
`);

export const CREATE_ENGAGEMENT = gengql(/* GraphQL */ `
  mutation createEngagement($input: CreateEngagementInput!) {
    createEngagement(input: $input) {
      id
      status
      primaryProjectId
    }
  }
`);

export const MAKE_ENGAGEMENT_OFFER = gengql(/* GraphQL */ `
  mutation makeEngagementOffer($input: MakeEngagementOfferInput!) {
    makeEngagementOffer(input: $input) {
      id
      status
      awaiting
      requiresGovernance
      fundingMethod
      nteHoursTotal
      expertOfferMadeAt
      expertRate {
        cents
        currency
      }
      expertEarnings {
        cents
        currency
      }
    }
  }
`);

export const ACCEPT_ENGAGEMENT_OFFER = gengql(/* GraphQL */ `
  mutation acceptEngagementOffer($input: AcceptEngagementOfferInput!) {
    acceptEngagementOffer(input: $input) {
      id
      status
      awaiting
      customerConfirmedAt
      governanceApprovedAt
    }
  }
`);

/**
 * The two exits from a pre-activation engagement: the expert declines, or the
 * customer creator withdraws. Both land in `canceled`, and both return the
 * engagement so Apollo's normalized cache moves the Hub to its new status
 * without a refetch.
 *
 * `expertDeclineNote` is deliberately not selected back — the api withholds it
 * from the expert, who is the only viewer that can call this.
 */
export const DECLINE_ENGAGEMENT_OFFER = gengql(/* GraphQL */ `
  mutation declineEngagementOffer($input: DeclineEngagementOfferInput!) {
    declineEngagementOffer(input: $input) {
      id
      status
      awaiting
      closureReason
      closedAt
    }
  }
`);

export const WITHDRAW_ENGAGEMENT_REQUEST = gengql(/* GraphQL */ `
  mutation withdrawEngagementRequest($input: WithdrawEngagementRequestInput!) {
    withdrawEngagementRequest(input: $input) {
      id
      status
      awaiting
      closureReason
      closedAt
    }
  }
`);

/**
 * Close an active engagement (ENG-5688).
 *
 * The api finalizes in one call — it never returns a `closing` engagement — so
 * the selection set is the same as the two pre-activation exits and Apollo's
 * normalized cache moves the Hub to `closed` on the response alone.
 *
 * Two refusals reach the caller as coded errors rather than as a generic
 * failure: `engagement.close_hours_pending` and
 * `engagement.close_period_unresolved`. Both are recoverable, so the caller
 * renders them as a next step.
 *
 * `viewerCanClose` is selected so the cache carries the api's own answer back:
 * the field is false on anything but ACTIVE, so the Settings section that gates
 * on it disappears the moment the close lands, with no client-side status rule
 * and no refetch.
 */
export const CLOSE_ENGAGEMENT = gengql(/* GraphQL */ `
  mutation closeEngagement($input: CloseEngagementInput!) {
    closeEngagement(input: $input) {
      id
      status
      awaiting
      closureReason
      closedAt
      updatedAt
      viewerCanClose
    }
  }
`);

/**
 * Persist the expert's dismissal of the §9.11 rate nudge (ENG-5604 G5).
 *
 * Server-side rather than local: a nudge the expert already declined should stay
 * declined on their next device and their next visit, and the ticket asked for
 * exactly that. `rateNudgeDismissed` on the engagement is the read side.
 */
export const DISMISS_ENGAGEMENT_RATE_NUDGE = gengql(/* GraphQL */ `
  mutation dismissEngagementRateNudge($input: DismissEngagementRateNudgeInput!) {
    dismissEngagementRateNudge(input: $input)
  }
`);

/**
 * The governance approver's decision. `governanceFeedback` is selected so the
 * normalized engagement never holds `awaiting='customer'` after a return
 * without the feedback that explains it, which every live surface reads as the
 * requester's plain "Review and accept the offer." (ENG-5752).
 */
export const SUBMIT_GOVERNANCE_DECISION = gengql(/* GraphQL */ `
  mutation submitGovernanceDecision($input: GovernanceDecisionInput!) {
    submitGovernanceDecision(input: $input) {
      id
      status
      awaiting
      governanceApprovedAt
      nteHoursTotal
      nteTotal {
        cents
        currency
      }
      governanceFeedback {
        kind
        note
        decidedAt
        proposedNteTotal {
          cents
          currency
        }
      }
    }
  }
`);

export const SEND_ENGAGEMENT_MESSAGE = gengql(/* GraphQL */ `
  mutation sendEngagementMessage($input: SendEngagementMessageInput!) {
    sendEngagementMessage(input: $input) {
      engagementId
      channelType
      senderId
      sentAt
    }
  }
`);

/**
 * The viewer's own engagements, bucketed (§9.6).
 *
 * Viewer-scoped and **viewer-aware since ENG-5609** — the same query serves the
 * expert and the customer side, returning each viewer's own engagements and
 * bucketing every row by what THAT viewer owes on it. `nextAction` is the
 * viewer's own to-do, so the two sides read different keys off one engagement,
 * and a viewer without the grant to act reads NONE rather than a CTA that
 * would 403.
 *
 * Every row here is one the viewer can also open: the api's customer predicate
 * mirrors the `engagement:view` grant set precisely so this list cannot emit a
 * dead link.
 *
 * `expertId` is selected to tell the expert's own rows from the customer's —
 * the only thing the status badge needs a role for. It is a plain column, so
 * it costs nothing, and it avoids selecting `participants` per row.
 *
 * Hours only, no money: on a list row `nteTotal` is customer-side and
 * `expertEarnings` expert-side, and a row is not worth the segregation
 * surface when hours already convey progress.
 *
 * `expertIdentity` (ENG-5730) is safe to select on a list for the same reason
 * it is on `ENGAGEMENTS_BY_PROJECT`: the resolver batches it for the whole page
 * (`stampPartyIdentities`, api's `engagement_read_models_gql.go`), so it costs
 * one profile lookup per page rather than one per row. Do not replace it with a
 * per-row read.
 */
export const MY_ENGAGEMENT_BUCKETS = gengql(/* GraphQL */ `
  query myEngagementBuckets {
    myEngagementBuckets {
      actionNeeded {
        id
        status
        expertId
        awaiting
        nteHoursTotal
        nteHoursConsumed
        hasPendingAction
        nextAction
        primaryProjectName
        expertIdentity {
          name
        }
      }
      active {
        id
        status
        expertId
        awaiting
        nteHoursTotal
        nteHoursConsumed
        hasPendingAction
        nextAction
        primaryProjectName
        expertIdentity {
          name
        }
      }
      closed {
        id
        status
        expertId
        awaiting
        nteHoursTotal
        nteHoursConsumed
        hasPendingAction
        nextAction
        primaryProjectName
        expertIdentity {
          name
        }
      }
    }
  }
`);

/**
 * Expert hours submission (FE3 / ENG-5449).
 *
 * The payload is not just the row: `amendmentRequired` and `surplusHours`
 * report the **submit-time split at NTE-remaining** (`hours.go:536-548`). Hours
 * above the remaining ceiling are not rejected — the portion that fits is
 * accepted and the surplus is handed back for an amendment. Only a submission
 * with nothing left to fit fails outright (`engagement.nte_exceeded`), so the
 * caller must render the accepted `submission` normally *and* surface the
 * surplus, rather than treating the flag as a failure.
 *
 * `hoursSubmitted` on the returned row is therefore the ACCEPTED hours, not
 * what the expert typed.
 */
export const SUBMIT_ENGAGEMENT_HOURS = gengql(/* GraphQL */ `
  mutation submitEngagementHours($input: SubmitEngagementHoursInput!) {
    submitEngagementHours(input: $input) {
      amendmentRequired
      surplusHours
      submission {
        id
        engagementId
        expertId
        status
        hoursSubmitted
        hoursConfirmed
        workDescription
        periodStart
        periodEnd
        submittedAt
        confirmedAt
        confirmedByUserId
        confirmNote
        disputeNote
        disputedByUserId
        resolution
        resolvedAt
        resolvedByUserId
        approvedAutomatically
        paidAt
      }
    }
  }
`);

/**
 * The work approver's three verbs on a `submitted` row (ENG-5449 / FE3).
 *
 * `mode` is `full | partial | dispute`; partial and dispute both require a
 * note, and partial also requires `hoursConfirmed`. Dispute moves the row to
 * `escalated` rather than resolving it — the resolution is a separate mutation.
 */
export const CONFIRM_ENGAGEMENT_HOURS = gengql(/* GraphQL */ `
  mutation confirmEngagementHours($input: ConfirmEngagementHoursInput!) {
    confirmEngagementHours(input: $input) {
      id
      status
      hoursSubmitted
      hoursConfirmed
      confirmedAt
      confirmedByUserId
      confirmNote
      disputeNote
      disputedByUserId
    }
  }
`);

/**
 * Resolving an escalated row (ENG-5449 / FE3).
 *
 * Work approver only — **not** `of_fulfillment`. FE1's `canResolveDispute`
 * gated OF staff, which is wrong against the wire (`hours_actor_not_work_approver`);
 * the OF-staff resolver and arbitration are both deferred, so no arbitration
 * flag is sent and none exists to send.
 *
 * `resolution` is the required note. `rejected` and `withdrawn` confirm zero
 * hours, and the api reads that back as `hoursConfirmed: null` — status carries
 * the story, so a caller must not render it as 0.
 */
export const RESOLVE_ENGAGEMENT_DISPUTE = gengql(/* GraphQL */ `
  mutation resolveEngagementDispute($input: ResolveEngagementDisputeInput!) {
    resolveEngagementDispute(input: $input) {
      id
      status
      hoursSubmitted
      hoursConfirmed
      resolution
      resolvedAt
      resolvedByUserId
    }
  }
`);

/**
 * The amendment loop (ENG-5449 / FE3).
 *
 * `changeSet` is the typed `EngagementAmendmentChangeSetInput` — scope,
 * nteHoursTotal, expertRate — and **no dates or pacing**: those are offer-time
 * terms with no amendable column, which is why the FE2 "DatePicker migration"
 * item has nothing to migrate here.
 *
 * All four return the amendment, but the engagement's own snapshot re-prices on
 * acceptance, so the Hub is refetched rather than cache-merged.
 */
export const PROPOSE_ENGAGEMENT_AMENDMENT = gengql(/* GraphQL */ `
  mutation proposeEngagementAmendment($input: ProposeEngagementAmendmentInput!) {
    proposeEngagementAmendment(input: $input) {
      id
      status
      proposedByUserId
      changeSet
      createdAt
    }
  }
`);

/** Resolves the current amendment as `countered` and opens a successor. */
export const COUNTER_ENGAGEMENT_AMENDMENT = gengql(/* GraphQL */ `
  mutation counterEngagementAmendment($input: CounterEngagementAmendmentInput!) {
    counterEngagementAmendment(input: $input) {
      id
      status
      proposedByUserId
      changeSet
      supersedesAmendmentId
      createdAt
    }
  }
`);

export const CONFIRM_ENGAGEMENT_AMENDMENT = gengql(/* GraphQL */ `
  mutation confirmEngagementAmendment($input: ConfirmEngagementAmendmentInput!) {
    confirmEngagementAmendment(input: $input) {
      id
      status
      resolvedAt
    }
  }
`);

export const WITHDRAW_ENGAGEMENT_AMENDMENT = gengql(/* GraphQL */ `
  mutation withdrawEngagementAmendment($input: WithdrawEngagementAmendmentInput!) {
    withdrawEngagementAmendment(input: $input) {
      id
      status
      resolvedAt
    }
  }
`);

/**
 * A project's engagements (ENG-5449 / FE3).
 *
 * **Hours, not money — and deliberately narrow.** The by-project list *would*
 * resolve customer-side money (it selects `e.*` and `ResolveViewerSide`
 * lazy-loads participants), but the section renders an hours-based NTE cell, so
 * asking for money would buy per-row viewer-side reads for nothing.
 *
 * Three fields are omitted on purpose:
 *   - `nteConsumedPercent` — derivable from the two hour figures already here.
 *   - `hoursSubmissions` — a per-row N+1 (`engagementHoursSubmissionsField`,
 *     api's `gql/service/schema/engagement_read_models_gql.go`).
 *   - `hasPendingAction` — expert-oriented: it resolves false at
 *     `pending_confirmation`, exactly when the CUSTOMER owes the confirmation.
 *     Rendering it to customers would be actively misleading.
 *
 * `nteHoursConsumed` stays: bounded per-row cost, named as B2 on ENG-5612.
 *
 * `requestScopeDescription` is what makes a row identifiable. It is a plain
 * column on the row the resolver already selects, so it is free, and it is the
 * customer's own words for what the engagement is — the field that tells two
 * engagements on the same project apart even when they share an expert.
 *
 * `expertIdentity` (ENG-5612 B3) is safe to select here for the same reason the
 * hour metrics are: the resolver batches it for the whole page
 * (`stampPartyIdentities`, api's `engagement_gql.go`), so it is one profile
 * lookup per page rather than one per row.
 *
 * **Do not select `customerIdentities` or `customerOrganizationName` here**
 * until the api hydrates this list. `ListProjectEngagements` loads bare rows,
 * without participants or the project's team, so as of api#1876 the first
 * resolves to the creator alone, one lookup per row, and the second to null.
 * The list would silently name fewer people than the Hub. Only
 * `ENGAGEMENT_HUB` selects them.
 *
 * Auth is per-page all-or-nothing (Decision 5): the resolver **denies as an
 * error**, not as an empty page.
 */
export const ENGAGEMENTS_BY_PROJECT = gengql(/* GraphQL */ `
  query engagementsByProject($projectId: ID!, $first: Int!) {
    engagementsByProject(projectId: $projectId, first: $first) {
      edges {
        cursor
        node {
          id
          status
          awaiting
          expertId
          expertIdentity {
            name
          }
          requestScopeDescription
          nteHoursTotal
          nteHoursConsumed
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`);

/**
 * The Billing tab's read (FE4 / ENG-5450, api ENG-5428).
 *
 * Fetched lazily when the tab opens, NOT folded into `ENGAGEMENT_HUB`:
 * `connectPayoutsEnabled` is a live, uncached Stripe round-trip on the api
 * (`FindAccountByUserID`), kept to a field-level resolver precisely so it costs
 * nothing unless selected — selecting it on every Hub load would put a Stripe
 * call on a hot path (the api's known Billing-tab N+1).
 *
 * Segregation is structural on the wire: `billing` resolves the viewer's side
 * ONCE and answers `customer` XOR `expert` as whole nullable objects — the
 * wrong side is null in its entirety, and neither type declares a take rate.
 * The client renders whichever arm came back and never re-derives the side.
 * `viewerSide` as an ARGUMENT is the OF-staff view override: the api rejects a
 * non-staff caller passing it (never a silent downgrade), so the UI only ever
 * sends it for internal staff. Per the header rule, no take-rate field exists
 * to select here — `bracketPosition` is index and hour bounds only.
 *
 * `stripeInvoiceId` is selected for one bit — a PENDING USD period WITH an
 * invoice id is "invoiced, awaiting payment" (normal for up to net-X days)
 * while one WITHOUT is "not yet invoiced" (transient). An id is not a link:
 * `hostedInvoiceUrl` is the link, and it is a BEARER capability gated on
 * `billing:view` — NOT on viewer side — so a customer-side viewer who is a
 * project member but not in the owning team gets null here, the same viewer
 * who already reads a null `creditBalance`. Its three nulls (credits row /
 * invoice not yet issued / withheld from this viewer) are not separable from
 * the row, so the client keys the Receipt cell on PRESENCE alone.
 *
 * `settledAt` is when the money landed, and is NOT a `closedAt` synonym —
 * `closedAt` is claim time. Same tick on the credit path, hours-to-days later
 * behind a shortfall retry, net-X days later on `usd_invoice`.
 */
export const ENGAGEMENT_BILLING = gengql(/* GraphQL */ `
  query engagementBilling($id: ID!, $viewerSide: EngagementBillingViewerSide) {
    engagement(id: $id) {
      id
      hasBillingActivity
      billing(viewerSide: $viewerSide) {
        viewerSide
        customer {
          fundingMethod
          chargeMechanism
          creditBalance {
            cents
            currency
          }
          hoursConsumed
          hoursRemaining
          bracketPosition {
            index
            cumulativeHours
            hoursToNext
            finalBracket
          }
          periods {
            id
            periodStart
            periodEnd
            closedAt
            status
            fundingMethod
            hoursConfirmed
            amount {
              cents
              currency
            }
            creditAmount {
              cents
              currency
            }
            stripeInvoiceId
            hostedInvoiceUrl
            settledAt
            failureReason
          }
        }
        expert {
          totalEarned {
            cents
            currency
          }
          maxEarnings {
            cents
            currency
          }
          connectPayoutsEnabled
          payments {
            id
            amount {
              cents
              currency
            }
            status
            kind
            paidAt
            hoursSubmissionId
          }
        }
      }
    }
  }
`);

/**
 * Notification bell — unread count (ENG-5451 / FE5).
 *
 * Deliberately its own query, separate from the feed. The bell is mounted in
 * the app header on every authenticated page, so the badge has to be cheap:
 * this is a server-side `COUNT(*)` (ENG-5435), and the feed below is not
 * fetched until the panel is actually opened.
 *
 * Scoped to `service: "engagement"` because that is the only domain FE5 wires.
 * The dashboard's own notification banners read the same table through the
 * legacy redux path and are unaffected.
 */
export const ENGAGEMENT_NOTIFICATIONS_UNREAD_COUNT = gengql(/* GraphQL */ `
  query engagementNotificationsUnreadCount {
    appNotificationsUnreadCount(service: "engagement")
  }
`);

/**
 * Notification bell — the feed (ENG-5451 / FE5).
 *
 * `engagement_id` filters server-side against the generated column added in
 * ENG-5435's migration, so the per-engagement filter is a WHERE clause rather
 * than the client fetching everything and narrowing in memory.
 *
 * The payload is read through an inline fragment on
 * `EngagementAppNotificationPayload`. Every field on it is nullable by design —
 * an event carries only the keys its own call site passed, so `submission_id`
 * is absent on a governance row and `decision` is absent on an hours row. Absent
 * is normal, not a defect.
 *
 * No names and no money here: display names come from the engagement read
 * (`ENGAGEMENT_NOTIFICATION_CONTEXT` below), which is viewer-aware and always
 * current, rather than from a snapshot taken when the notification was written.
 */
export const ENGAGEMENT_NOTIFICATIONS = gengql(/* GraphQL */ `
  query engagementNotifications($engagementId: String, $limit: Int, $offset: Int) {
    appNotifications(
      service: "engagement"
      engagement_id: $engagementId
      limit: $limit
      offset: $offset
    ) {
      id
      code
      created_at
      read_at
      payload {
        ... on EngagementAppNotificationPayload {
          engagement_id
          hub_tab
          subject
          submission_id
          hours_submitted
          hours_confirmed
          outcome
          period_start
          period_end
          confirm_note
          dispute_note
          resolution
          decline_note
          decision
          governance_note
          threshold_pct
          nte_hours_total
          hours_confirmed_total
        }
      }
    }
  }
`);

/**
 * The engagements a bell feed spans, for row context and the filter control.
 *
 * This is the hydration half of the FE5 design: the notification payload
 * carries ids and event facts, and the display names — expert, project — come
 * from here. Two reasons it is a separate read rather than payload fields.
 * A name written into a payload is frozen at send time and goes stale on the
 * next rename; and this query is viewer-aware, so each side sees only what it
 * is entitled to, which a denormalized copy could not guarantee.
 *
 * Rows the viewer can no longer read simply do not come back, and the bell
 * falls back to the notification's own `subject` for those.
 *
 * `expertId` is selected for a different reason than the names: it is what
 * resolves which side of each engagement the viewer is on, per engagement, so
 * the role-specific copy branches fire. Without it every row renders as the
 * customer side.
 *
 * `createdAt` is the filter's tie-breaker (ENG-5451 AC5). The label is the
 * project name, so a project carrying several engagements yields several
 * identical options; the expert's name separates them for a customer-side
 * viewer, and for an expert — who is the expert on all of them — the open date
 * is what is left. It is a plain non-null column, so it costs nothing here.
 */
export const ENGAGEMENT_NOTIFICATION_CONTEXT = gengql(/* GraphQL */ `
  query engagementNotificationContext {
    myEngagementBuckets {
      actionNeeded {
        id
        createdAt
        expertId
        primaryProjectName
        expertIdentity {
          name
        }
      }
      active {
        id
        createdAt
        expertId
        primaryProjectName
        expertIdentity {
          name
        }
      }
      closed {
        id
        createdAt
        expertId
        primaryProjectName
        expertIdentity {
          name
        }
      }
    }
  }
`);

/**
 * Mark bell rows read (ENG-5451 / FE5).
 *
 * The mutation already existed and already took a list, so "mark all read" and
 * a single row activation are the same call with a different-length array — the
 * prototype's session-only `locallyRead` set is replaced by this, not
 * supplemented by it.
 */
export const READ_APP_NOTIFICATIONS = gengql(/* GraphQL */ `
  mutation readAppNotifications($ids: [String]) {
    readAppNotifications(ids: $ids) {
      id
      read_at
    }
  }
`);

/** Dismiss one bell row (ENG-5451 / FE5). Server-persisted, unlike the prototype. */
export const DISMISS_APP_NOTIFICATION = gengql(/* GraphQL */ `
  mutation dismissAppNotification($id: String!) {
    dismissAppNotification(id: $id) {
      id
      dismissed_at
    }
  }
`);

/**
 * The OF staff feed — every engagement on the platform (ENG-5684 / FE6).
 *
 * Backed by ENG-5438's `ListStaffEngagements`, which gates on a direct
 * internal-team check and denies a non-staff caller with a permission error
 * rather than an empty page. The tab that mounts this is hidden for non-staff,
 * so a denial here means a stale session, not a snoop.
 *
 * `sort: newest` is passed explicitly. The api defaults to `red_flags` because
 * it was built as a monitoring queue; the staff tab is a browsing surface and
 * opens newest-first, matching /expert_requests.
 *
 * `redFlags` resolves null on every other Engagement query by design — the
 * absence of a per-row fallback is what keeps operational flags staff-only.
 * Asking for it anywhere else would get nulls, not a leak, but this is the one
 * surface entitled to ask.
 *
 * No take-rate or expert-side money field is declared, per this file's §6.3
 * merge gate. Staff read the roster here and open the Hub for money.
 */
export const STAFF_ENGAGEMENTS = gengql(/* GraphQL */ `
  query staffEngagements($bucket: EngagementFeedBucket, $first: Int!, $after: String) {
    engagements(bucket: $bucket, sort: newest, first: $first, after: $after) {
      pageInfo {
        hasNextPage
        total
      }
      edges {
        cursor
        node {
          id
          status
          awaiting
          nteHoursTotal
          nteHoursConsumed
          expertLastActivityAt
          updatedAt
          primaryProjectName
          primaryProject {
            id
            name
            group {
              id
              name
            }
          }
          expertIdentity {
            userId
            name
          }
          redFlags {
            flags
            count
            billingFailureReason
          }
        }
      }
    }
  }
`);

/**
 * The customer power user's org-wide roll-up (ENG-5436 / FE6).
 *
 * The scope is NOT an argument: the api derives it from the viewer's own
 * owner/admin `team_members` rows, and a viewer with no such row is DENIED with
 * `permission.not_allowed` rather than handed an empty page. So there is no
 * client-side gate to write, and the absence of a filter for "which account" is
 * deliberate rather than missing.
 *
 * `workApproverId` is deliberately not offered. It matches the FROZEN
 * participant row, which disagrees with the live project-owner set the moment
 * ownership moves, so a filter labelled "work approver" would hide rows whose
 * displayed work approver is exactly the person filtered for.
 *
 * No sort argument, because the api has none: ordering is most-recently-active
 * first and "needs my action" arrives per row as `hasPendingAction`.
 */
export const ACCOUNT_ENGAGEMENTS = gengql(/* GraphQL */ `
  query accountEngagements(
    $status: [String!]
    $expertId: ID
    $projectId: ID
    $createdAfter: Datetime
    $first: Int!
    $after: String
  ) {
    accountEngagements(
      status: $status
      expertId: $expertId
      projectId: $projectId
      createdAfter: $createdAfter
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        total
      }
      edges {
        cursor
        node {
          id
          status
          awaiting
          hasPendingAction
          nextAction
          nteHoursTotal
          nteHoursConsumed
          createdAt
          primaryProjectName
          primaryProject {
            id
            name
          }
          expertIdentity {
            userId
            name
          }
        }
      }
    }
  }
`);

/**
 * Does this viewer hold the org scope at all? The roll-up tab's gate.
 *
 * A COUNT, not a page. `accountEngagementStats` is scoped and denied exactly
 * like `accountEngagements` (same derived `team_members` relation, same
 * `permission.not_allowed`), and selecting only `activeCount` resolves through
 * the api's `loadCounts` alone, so the gate costs an aggregate rather than a
 * page of hydrated rows.
 *
 * Split from the options query for the reason `CAN_APPROVE_GOVERNANCE` is split
 * from `MY_GOVERNANCE_QUEUE` above: this runs on every visit to `/engagements`,
 * for every viewer, and most of them are asking a question whose answer is no.
 * Deciding whether to draw a tab must not cost a list read.
 *
 * The count itself is unused. It is the cheapest non-empty selection the type
 * offers, and the denial is the whole signal.
 */
export const ACCOUNT_ENGAGEMENT_GATE = gengql(/* GraphQL */ `
  query accountEngagementGate {
    accountEngagementStats {
      activeCount
    }
  }
`);

/**
 * The roll-up's filter options.
 *
 * The expert and project facets are ID arguments, so the pickers need id/name
 * pairs from somewhere, and they cannot come from the filtered list itself: pick
 * an expert and that list holds exactly one expert, which collapses the picker
 * the moment it is used.
 *
 * Fired by the roll-up itself, so it costs nothing until the tab is opened.
 * Unfiltered and capped at one page: the options are complete for an account
 * with up to PAGE_SIZE-many engagements, which covers the staged rollout
 * (ENG-5446). Beyond that the pickers list the most recently active, which is
 * the same order the roll-up itself pages in, and the list says so.
 */
export const ACCOUNT_ENGAGEMENT_FACETS = gengql(/* GraphQL */ `
  query accountEngagementFacets($first: Int!) {
    accountEngagements(first: $first) {
      pageInfo {
        total
      }
      edges {
        node {
          id
          primaryProject {
            id
            name
          }
          expertIdentity {
            userId
            name
          }
        }
      }
    }
  }
`);

/**
 * Open a bilateral channel with OnFrontiers (ENG-5439 / FE6).
 *
 * Idempotent server-side, and it returns the channel either way, so a double
 * click cannot create two. The caller still has to re-read ENGAGEMENT_HUB
 * afterwards: `selectableChannels` renders from `messageChannels`, which is
 * where the wall filtering happens, and a channel this mutation just created is
 * absent from the cached copy.
 */
export const OPEN_ENGAGEMENT_DIRECT_CHANNEL = gengql(/* GraphQL */ `
  mutation openEngagementDirectChannel($input: OpenEngagementDirectChannelInput!) {
    openEngagementDirectChannel(input: $input) {
      id
      channelType
    }
  }
`);

/**
 * The projects the viewer may open an engagement on, in the account they are
 * acting in.
 *
 * The options for the one Hire flow that starts without a project (ENG-5452 /
 * FE6): `from_profile`, where the customer arrives with an expert in hand and
 * the drawer has to ask. The prototype's
 * `HireExpertDrawer/ProjectSection.tsx` is the design.
 *
 * The filters here are not a UI preference — they are the api's own
 * `engagement:create` grant, restated. That policy is one predicate
 * (`engagement/service/engagement_policies.go`, "project member can create
 * engagement"): an active `project_members` row for the viewer on that project.
 * No expert-request condition, no role condition. `member_only: true` IS that
 * predicate, which is why it is the only project-level filter.
 *
 * `open_only: true` used to be here too, and it was a silent bug (ENG-5729).
 * The api turns that flag into an INNER JOIN on `expert_requests` with
 * `er.state <> 'closed'` (`project/service/project_service.go`), so a project
 * whose expert requests are all closed — or which has none yet — produced zero
 * rows and vanished from the picker, with no empty state and no explanation,
 * even for its Owner. It is a browsing filter for project LISTS ("what is
 * live"), and it answers a different question than "may I hire here": a closed
 * expert request says nothing about whether this project can host an
 * engagement, and the api will happily create one there.
 *
 * `group_id` is REQUIRED by this query, not optional, and that is the other
 * half of ENG-5729. Without it `FindProjects` applies no `p.team_id` filter and
 * returns every project the viewer is a member of across every account, so the
 * picker offered projects from accounts the viewer was not acting in. Choosing
 * one is a trap rather than a convenience: the engagement would be created
 * under THAT account's billing settings, and an account without an
 * `engagement_billing_settings` row answers `billing_settings_not_enabled` —
 * which this drawer treats as terminal. Multi-account users are classified by
 * the account they are operating in (ENG-5351), so the picker is too.
 *
 * `first` is the server maximum (`paging.ValidatePage` caps it at 1000) because
 * this is a native `<select>`, and a native select cannot paginate — there is
 * nowhere to put a "load more" inside an options list. One request that cannot
 * truncate is the whole pagination story. Reachable in practice because the set
 * is doubly narrowed: one account, and only projects the viewer created or was
 * invited to (project creation adds the creator alone, as Owner). The
 * unscoped-and-unpaginated dashboard read is the same shape at the same size
 * (`routes/dashboard/index.tsx`).
 *
 * Order is the api's `p.created_at DESC` — newest project first, deliberately
 * unsorted here.
 *
 * None of this makes the client the authority. The server still decides on
 * submit: a project it refuses answers `engagement.project_account_mismatch` or
 * a permission error. Matching the grant here is about not hiding a project the
 * server would have accepted, not about pre-approving one.
 */
export const HIRE_EXPERT_PROJECT_OPTIONS = gengql(/* GraphQL */ `
  query hireExpertProjectOptions($first: Int!, $groupId: String!) {
    projects(first: $first, member_only: true, group_id: $groupId) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`);

/**
 * The account's engagement minimum, read without a project (ENG-5735).
 *
 * `engagementCreationContext` already carries this number, but it is keyed on a
 * project, and `from_profile` is the one creation path that opens with no
 * project chosen. The field copy states the floor unconditionally (FB-181), so
 * on that path there has to be an answer before the picker is touched.
 *
 * The minimum is a property of the billing ACCOUNT, and the picker only offers
 * projects from the account the viewer is acting in, so this read and the
 * project-keyed one resolve to the same number. The project answer still wins
 * once it lands, because the project is what the engagement is billed to.
 */
export const HIRE_EXPERT_ACCOUNT_MIN_HOURS = gengql(/* GraphQL */ `
  query hireExpertAccountMinHours($groupId: String!) {
    group(id: $groupId) {
      id
      billing_account {
        id
        engagementBillingSettings {
          billingAccountId
          engagementMinHours
        }
      }
    }
  }
`);
